/**
 * Native persistence (and the web fallback without IndexedDB): AsyncStorage
 * with one key per state section and one key per game. No single value
 * grows with history, so Android's ~2 MB per-row read limit never applies.
 */
import AsyncStorage from "@react-native-async-storage/async-storage";

import { CorruptRecord, type HistoryKey, type StorageAdapter } from "./types";

export const STATE_PREFIX = "synapse_state_";
export const HISTORY_PREFIX = "synapse_history_";

export const historyStorageKey = (key: HistoryKey): string =>
  `${HISTORY_PREFIX}${key}`;

const decode = (raw: string | null): unknown => {
  if (raw === null) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return new CorruptRecord(raw);
  }
};

async function keysWith(prefix: string): Promise<string[]> {
  return (await AsyncStorage.getAllKeys()).filter((k) => k.startsWith(prefix));
}

async function readPrefixed(prefix: string): Promise<[string, unknown][]> {
  const keys = await keysWith(prefix);
  if (keys.length === 0) return [];
  const pairs = await AsyncStorage.multiGet(keys);
  return pairs
    .map(([k, raw]): [string, unknown] => [k.slice(prefix.length), decode(raw)])
    .filter(([, v]) => v !== undefined);
}

async function writePrefixed(
  prefix: string,
  records: [string | number, unknown][],
): Promise<void> {
  if (records.length === 0) return;
  await AsyncStorage.multiSet(
    records.map(([k, v]) => [`${prefix}${k}`, JSON.stringify(v)]),
  );
}

async function removePrefixed(
  prefix: string,
  keys: (string | number)[],
): Promise<void> {
  if (keys.length === 0) return;
  await AsyncStorage.multiRemove(keys.map((k) => `${prefix}${k}`));
}

export function createNativeAdapter(): StorageAdapter {
  return {
    readState: async () => new Map(await readPrefixed(STATE_PREFIX)),
    writeState: (records) => writePrefixed(STATE_PREFIX, records),
    deleteState: (names) => removePrefixed(STATE_PREFIX, names),
    readHistory: async () =>
      (await readPrefixed(HISTORY_PREFIX))
        .map(([k, v]): [HistoryKey, unknown] => [Number(k), v])
        .filter(([k]) => Number.isSafeInteger(k))
        .sort((a, b) => a[0] - b[0]),
    writeHistory: (records) => writePrefixed(HISTORY_PREFIX, records),
    deleteHistory: (keys) => removePrefixed(HISTORY_PREFIX, keys),
    clear: async () => {
      const keys = [
        ...(await keysWith(HISTORY_PREFIX)),
        ...(await keysWith(STATE_PREFIX)),
      ];
      if (keys.length > 0) await AsyncStorage.multiRemove(keys);
    },
  };
}
